import { useMemo } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { query as sq, queryWithCount } from "@/lib/supabase-utils";
import { localDateStartIso, localDateEndIso } from "@/lib/utils";
import type {
  ClientBasic,
  ProductMinimal,
  Product,
  Movement as MovementType,
  AmazonOrder as AmazonOrderType,
  OrderMovement as OrderMovementType,
  MovementsFilters,
} from "@/types";

// Re-export types for backward compatibility
export type ClientRow = ClientBasic;
export type ProductRow = ProductMinimal;
export type DashboardProduct = Product;
export type MovementRow = MovementType;
export type AmazonOrderRow = AmazonOrderType;
export type OrderMovementRow = OrderMovementType;
export type { MovementsFilters } from "@/types";

// ── Shared: clients list ──────────────────────────────────────────
export const useClients = () =>
  useQuery<ClientBasic[]>({
    queryKey: ["clients"],
    queryFn: () => sq(supabase.from("clients").select("id, name, alias, cif, address, numero_cuenta").order("name")) as Promise<ClientBasic[]>,
    staleTime: 5 * 60 * 1000,
  });

// ── Shared: current user role ─────────────────────────────────────
export const useUserRole = () =>
  useQuery<string>({
    queryKey: ["userRole"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return "user";
      const data = await sq(supabase.from("user_roles").select("role").eq("user_id", user.id).single());
      return (data as any)?.role || "user";
    },
    staleTime: 5 * 60 * 1000,
  });

// ── Shared: product list (id, name, sku) ──────────────────────────
export const useProductList = () =>
  useQuery<ProductMinimal[]>({
    queryKey: ["productList"],
    queryFn: () => sq(supabase.from("products").select("id, name, sku").order("name")) as Promise<ProductMinimal[]>,
    staleTime: 5 * 60 * 1000,
  });

// ── Dashboard: products with clients ──────────────────────────────
// Two-phase loading: una query rápida con campos esenciales para pintar
// la tabla al instante, y una query completa en paralelo con joins y
// campos auxiliares. El consumidor ve `full ?? fast`.
//
// La fast query NO hace join a clients (el join a tabla foránea es lo
// que más ralentiza). En su lugar, hidratamos el campo `clients` desde
// el cache de `useClients` que ya se prefetcheó en DashboardLayout tras
// el login. Si ese cache no está disponible, cae a un placeholder.
const DASHBOARD_FAST_COLUMNS =
  "id, name, sku, stock, image_url, asin, barcode, client_id, expiry_date, lote, observations, estanteria, modulo, nivel";
const DASHBOARD_FULL_COLUMNS = DASHBOARD_FAST_COLUMNS + ", clients(name, alias)";

export const useDashboardProducts = () => {
  const qc = useQueryClient();

  const fast = useQuery<any[]>({
    queryKey: ["dashboardProducts", "fast"],
    queryFn: () =>
      sq(
        (supabase as any)
          .from("products")
          .select(DASHBOARD_FAST_COLUMNS)
          .order("name")
          .limit(300)
      ),
    staleTime: 10 * 60 * 1000,
  });

  const full = useQuery<any[]>({
    queryKey: ["dashboardProducts", "full"],
    queryFn: () =>
      sq(
        (supabase as any)
          .from("products")
          .select(DASHBOARD_FULL_COLUMNS)
          .order("name")
          .limit(5000)
      ),
    staleTime: 10 * 60 * 1000,
  });

  // Hidrata clients en la fast data a partir del cache de useClients.
  const fastHydrated = useMemo<Product[]>(() => {
    if (!fast.data) return [];
    const clientsList = qc.getQueryData<ClientBasic[]>(["clients"]) ?? [];
    const clientMap = new Map<string, { name: string; alias: string | null }>();
    for (const c of clientsList) {
      clientMap.set(c.id, { name: c.name, alias: c.alias ?? null });
    }
    return fast.data.map((p: any) => ({
      ...p,
      clients: clientMap.get(p.client_id) ?? { name: "-", alias: null },
    })) as Product[];
  }, [fast.data, qc]);

  const data: Product[] = (full.data as Product[] | undefined) ?? fastHydrated;

  return {
    data,
    isLoading: !fast.data && !full.data && (fast.isLoading || full.isLoading),
    isFullLoaded: !!full.data,
  };
};

// ── Inventory (server-side paginated) ────────────────────────────
import type { InventoryFilters } from "@/types";
import { normalize as normalizeForSearch } from "@/lib/search";

const INVENTORY_COLUMNS =
  "id, name, sku, stock, image_url, asin, barcode, client_id, expiry_date, lote, observations, estanteria, modulo, nivel, base_product_id, clients(name, alias)";

const applyInventoryUserFilters = (query: any, filters?: InventoryFilters) => {
  if (filters?.search) {
    // Normalizamos el término igual que las columnas *_norm (sin tildes,
    // minúsculas) para que los índices GIN pg_trgm se usen y la búsqueda sea
    // insensible a acentos/mayúsculas. Escapamos comas y paréntesis por
    // seguridad del parser de PostgREST.
    const s = normalizeForSearch(filters.search);
    if (s) query = query.or(`sku_norm.ilike.%${s}%,name_norm.ilike.%${s}%,barcode_norm.ilike.%${s}%`);
  }
  if (filters?.clientId) query = query.eq("client_id", filters.clientId);
  if (filters?.estanteria) query = query.eq("estanteria", filters.estanteria);
  if (filters?.modulo) query = query.eq("modulo", filters.modulo);
  if (filters?.nivel) query = query.eq("nivel", filters.nivel);
  if (filters?.lowStock) query = query.gt("stock", 0).lte("stock", 5);
  if (filters?.noImage) query = query.is("image_url", null).not("asin", "is", null);
  if (filters?.expiring) {
    const oneMonth = new Date();
    oneMonth.setMonth(oneMonth.getMonth() + 1);
    query = query.not("expiry_date", "is", null).lte("expiry_date", oneMonth.toISOString().split("T")[0]);
  }
  return query;
};

const applyInventoryFilters = (query: any, filters?: InventoryFilters) => {
  query = query.is("base_product_id", null);
  return applyInventoryUserFilters(query, filters);
};

// Trae TODO el inventario que cumple los filtros actuales (sin paginar), en
// lotes de 1000. Lo usa la exportación a Excel para no exportar solo la página
// visible. Respeta los mismos filtros y orden que el listado.
export const fetchAllInventory = async (filters?: InventoryFilters): Promise<Product[]> => {
  const sortCol = filters?.sortField || "name";
  const sortAsc = filters?.sortDir !== "desc";
  const batchSize = 1000;
  let offset = 0;
  const all: Product[] = [];
  while (true) {
    let query = (supabase as any)
      .from("products")
      .select(INVENTORY_COLUMNS)
      .order(sortCol, { ascending: sortAsc });
    query = applyInventoryFilters(query, filters).range(offset, offset + batchSize - 1);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data || []) as Product[];
    all.push(...rows);
    if (rows.length < batchSize) break;
    offset += batchSize;
  }
  return all;
};

export const useInventory = (filters?: InventoryFilters) => {
  const page = filters?.page ?? 1;
  const pageSize = filters?.pageSize ?? 30;

  const sortCol = filters?.sortField || "name";
  const sortAsc = filters?.sortDir !== "desc";

  const dataQuery = useQuery<Product[]>({
    queryKey: ["inventory", "data", filters],
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = (supabase as any)
        .from("products")
        .select(INVENTORY_COLUMNS)
        .order(sortCol, { ascending: sortAsc });

      query = applyInventoryFilters(query, filters).range(from, to);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Product[];
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const filtersWithoutPage = { ...filters, page: undefined, pageSize: undefined, sortField: undefined, sortDir: undefined };

  const countQuery = useQuery<number>({
    queryKey: ["inventory", "count", filtersWithoutPage],
    queryFn: async () => {
      let query = (supabase as any)
        .from("products")
        .select("id", { count: "exact", head: true });
      query = applyInventoryUserFilters(query, filters);
      const { count, error } = await query;
      if (error) throw error;
      return count ?? 0;
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
  const stockSumQuery = useQuery<number>({
    queryKey: ["inventory", "stockSum", filtersWithoutPage],
    queryFn: async () => {
      let allRows: any[] = [];
      const batchSize = 1000;
      let offset = 0;
      while (true) {
        let query = (supabase as any)
          .from("products")
          .select("stock");
        query = applyInventoryUserFilters(query, filters).range(offset, offset + batchSize - 1);
        const { data: rows, error } = await query;
        if (error) throw error;
        if (!rows || rows.length === 0) break;
        allRows = allRows.concat(rows);
        if (rows.length < batchSize) break;
        offset += batchSize;
      }
      return allRows.reduce((sum: number, r: any) => sum + (r.stock || 0), 0);
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const data = dataQuery.data ?? [];
  const heuristicTotal = data.length === pageSize ? page * pageSize + 1 : (page - 1) * pageSize + data.length;
  const totalCount = countQuery.data ?? heuristicTotal;

  const visibleIds = data.map((p) => p.id);
  const childCountQuery = useQuery<Record<string, number>>({
    queryKey: ["inventory", "childCount", visibleIds],
    enabled: visibleIds.length > 0,
    queryFn: async () => {
      const { data: rows, error } = await (supabase as any)
        .from("products")
        .select("base_product_id")
        .in("base_product_id", visibleIds);
      if (error) throw error;
      const map: Record<string, number> = {};
      (rows || []).forEach((r: any) => {
        if (r.base_product_id) map[r.base_product_id] = (map[r.base_product_id] || 0) + 1;
      });
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    data,
    totalCount,
    totalStock: stockSumQuery.data ?? 0,
    isLoading: dataQuery.isLoading,
    childCountMap: childCountQuery.data ?? {},
  };
};

// ── Movements (server-side paginated) ─────────────────────────────
const MOVEMENTS_COLUMNS =
  "id, type, quantity, notes, referencia, batch_id, tracking_number, destinatario, destinatario_direccion, destinatario_telefono, marketplace, albaran_url, created_at, client_id, product_id, products(name, sku, image_url), clients(name, alias)";

const applyMovementFilters = (query: any, filters?: MovementsFilters) => {
  // Default window is 30 days for performance. If the user provides an explicit
  // startDate, honour it — otherwise the cap would silently truncate older results.
  if (!filters?.startDate) {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    query = query.gte("created_at", thirtyDaysAgo);
  }

  if (filters?.type && filters.type !== "all") {
    query = query.eq("type", filters.type);
  }
  if (filters?.clientId && filters.clientId !== "all") {
    query = query.eq("client_id", filters.clientId);
  }
  if (filters?.productId) {
    query = query.eq("product_id", filters.productId);
  }
  if (filters?.marketplace && filters.marketplace !== "all") {
    if (filters.marketplace === "Sin marketplace") {
      query = query.is("marketplace", null);
    } else {
      query = query.eq("marketplace", filters.marketplace);
    }
  }
  if (filters?.startDate) {
    query = query.gte("created_at", localDateStartIso(filters.startDate));
  }
  if (filters?.endDate) {
    query = query.lte("created_at", localDateEndIso(filters.endDate));
  }
  if (filters?.search) {
    const s = normalizeForSearch(filters.search);
    if (s) query = query.or(
      `notes_norm.ilike.%${s}%,referencia_norm.ilike.%${s}%,tracking_norm.ilike.%${s}%,destinatario_norm.ilike.%${s}%`
    );
  }
  return query;
};

export const useMovements = (filters?: MovementsFilters) => {
  const page = filters?.page ?? 1;
  const pageSize = filters?.pageSize ?? 30;

  // Paso 1: llamar a get_movements_grouped (RPC) — agrupa y pagina en SQL.
  // Fallback al bucle histórico (batches de 1000) si la RPC no existe o falla,
  // para no romper entornos donde aún no se aplicó la migración.
  const metaQuery = useQuery<{ pageGroups: Array<{ key: string; ids: string[] }>; totalGroups: number }>({
    queryKey: ["movements", "meta-rpc", filters, page, pageSize],
    queryFn: async () => {
      const defaultStart = !filters?.startDate
        ? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
        : localDateStartIso(filters.startDate);
      const endIso = filters?.endDate ? localDateEndIso(filters.endDate) : null;

      const rpcParams: Record<string, unknown> = {
        p_type:        filters?.type && filters.type !== "all" ? filters.type : null,
        p_client_id:   filters?.clientId && filters.clientId !== "all" ? filters.clientId : null,
        p_product_id:  filters?.productId ?? null,
        p_marketplace: filters?.marketplace && filters.marketplace !== "all"
          ? (filters.marketplace === "Sin marketplace" ? "__NULL__" : filters.marketplace)
          : null,
        p_start_date:  defaultStart,
        p_end_date:    endIso,
        p_search:      filters?.search ?? null,
        p_page:        page,
        p_page_size:   pageSize,
      };

      const { data: rpcRows, error: rpcErr } = await (supabase as any).rpc("get_movements_grouped", rpcParams);
      if (!rpcErr && Array.isArray(rpcRows)) {
        const pageGroups = rpcRows.map((r: any) => ({ key: r.group_key, ids: r.ids || [] }));
        const totalGroups = Number(rpcRows[0]?.total_groups) || 0;
        return { pageGroups, totalGroups };
      }

      // Fallback legacy: bucle de batches en cliente
      const batchSize = 1000;
      let offset = 0;
      const all: Array<{ id: string; referencia: string | null; batch_id: string | null; destinatario: string | null; created_at: string }> = [];
      while (true) {
        let q = supabase
          .from("movements")
          .select("id, referencia, batch_id, destinatario, created_at")
          .order("created_at", { ascending: false });
        q = applyMovementFilters(q, filters).range(offset, offset + batchSize - 1);
        const { data, error } = await q;
        if (error) throw error;
        const rows = (data as any[]) || [];
        all.push(...rows);
        if (rows.length < batchSize) break;
        offset += batchSize;
      }
      const order: string[] = [];
      const map = new Map<string, string[]>();
      for (const row of all) {
        // Misma clave que groupMovementsByReference (Movements.tsx) y el RPC:
        // batch_id → referencia+día → destinatario+día → id.
        const day = (row.created_at || "").slice(0, 10);
        const key = row.batch_id
          ? `batch:${row.batch_id}`
          : row.referencia
            ? `ref:${row.referencia}:${day}`
            : row.destinatario
              ? `dest:${row.destinatario}:${day}`
              : `id:${row.id}`;
        if (!map.has(key)) { map.set(key, []); order.push(key); }
        map.get(key)!.push(row.id);
      }
      const groups = order.map((k) => ({ key: k, ids: map.get(k)! }));
      const from = (page - 1) * pageSize;
      return {
        pageGroups: groups.slice(from, from + pageSize),
        totalGroups: groups.length,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const pageGroups = metaQuery.data?.pageGroups ?? [];
  const totalCount = metaQuery.data?.totalGroups ?? 0;
  const pageIds = pageGroups.flatMap((g) => g.ids);

  // Paso 2: traer los movimientos completos con joins sólo de los IDs visibles
  const dataQuery = useQuery<MovementType[]>({
    queryKey: ["movements", "data", pageIds],
    enabled: pageIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("movements")
        .select(MOVEMENTS_COLUMNS)
        .in("id", pageIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as any) || [];
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  // Si el paso 1 no devolvió ningún grupo (p.ej. un cliente sin movimientos en
  // la ventana de fechas), dataQuery queda deshabilitada y `keepPreviousData`
  // conservaría las filas del filtro anterior. Forzamos lista vacía para que la
  // tabla refleje el resultado real y no se quede "pegada" al cliente previo.
  const data = pageIds.length > 0 ? (dataQuery.data ?? []) : [];

  return {
    data: { data, totalCount } as { data: MovementType[]; totalCount: number },
    isLoading: metaQuery.isLoading || dataQuery.isLoading,
    error: metaQuery.error || dataQuery.error,
  } as any;
};

// ── Orders (combined amazon_orders + movements) ───────────────────
// Trae todas las filas del rango en lotes de 1000 (PostgREST capa las
// respuestas, y el histórico completo supera un único request).
const fetchAllRows = async (buildQuery: (from: number, to: number) => any) => {
  const batchSize = 1000;
  const all: any[] = [];
  for (let offset = 0; ; offset += batchSize) {
    const rows = (await sq(buildQuery(offset, offset + batchSize - 1))) as any[];
    all.push(...(rows || []));
    if (!rows || rows.length < batchSize) break;
  }
  return all;
};

export const useOrders = (startDate?: string) =>
  useQuery<{ amazonOrders: AmazonOrderType[]; movements: OrderMovementType[] }>({
    queryKey: ["orders", startDate ?? null],
    queryFn: async () => {
      // Sin fecha de inicio se trae el histórico completo. Con fecha, se baja
      // como mínimo 30 días para que la búsqueda por referencia/tracking
      // (que ignora el filtro de fechas) siga encontrando pedidos recientes.
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const startIso = startDate
        ? (() => {
            const iso = localDateStartIso(startDate);
            return iso < thirtyDaysAgo ? iso : thirtyDaysAgo;
          })()
        : null;

      const [amazonOrders, movements] = await Promise.all([
        fetchAllRows((from, to) => {
          let q = supabase
            .from("amazon_orders")
            .select("id, order_number, tracking_number, buyer_name, created_at, quantity, status, transportista, products(name, sku), clients(alias)")
            .order("created_at", { ascending: false });
          if (startIso) q = q.gte("created_at", startIso);
          return q.range(from, to);
        }),
        fetchAllRows((from, to) => {
          let q = supabase
            .from("movements")
            .select("id, type, quantity, notes, referencia, tracking_number, destinatario, transportista, created_at, products(name, sku), clients(alias)")
            .eq("type", "salida")
            .order("created_at", { ascending: false });
          if (startIso) q = q.gte("created_at", startIso);
          return q.range(from, to);
        }),
      ]);

      return {
        amazonOrders: (amazonOrders as any) || [],
        movements: (movements as any) || [],
      };
    },
    staleTime: 5 * 60 * 1000,
  });

// Bloque de Shipments (Sendcloud) eliminado: la app ya no consume datos de
// sendcloud_shipments / sendcloud_client_mappings. Los envíos vienen de
// amazon_orderdesk_orders y movements (ver useOverviewStats / useOrders).

// ── Clients (full) ────────────────────────────────────────────────
import type { ClientFull } from "@/types";

export const useClientsFull = () =>
  useQuery<ClientFull[]>({
    queryKey: ["clientsFull"],
    queryFn: () => sq((supabase as any).from("clients").select("*").order("name")) as Promise<ClientFull[]>,
    staleTime: 10 * 60 * 1000,
  });

// ── Invoices ─────────────────────────────────────────────────────
export interface InvoiceRow {
  id: string;
  invoice_number: string;
  client_id: string;
  issue_date: string;
  due_date: string | null;
  total_amount: number;
  status: string;
  pdf_url: string | null;
  notes: string | null;
  created_at: string;
  clients: { name: string };
}

export const useInvoices = () =>
  useQuery<InvoiceRow[]>({
    queryKey: ["invoices"],
    queryFn: () => sq(supabase.from("invoices").select("*, clients(name)").order("issue_date", { ascending: false })) as Promise<InvoiceRow[]>,
    staleTime: 5 * 60 * 1000,
  });

// ── Overview stats (multiple parallel queries) ───────────────────
import { startOfDay, subDays } from "date-fns";

export const useOverviewStats = () =>
  useQuery({
    queryKey: ["overviewStats"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      // Path rápido: vista materializada refrescada por pg_cron cada 5 min.
      // Fallback al cálculo directo si la RPC no existe o falla (backwards
      // compat con entornos donde aún no se aplicó la migración).
      const { data: rpcRows, error: rpcErr } = await (supabase as any).rpc("get_overview_stats");
      if (!rpcErr && Array.isArray(rpcRows) && rpcRows.length > 0) {
        const r = rpcRows[0];
        return {
          totalProducts: Number(r.total_products) || 0,
          totalStock: Number(r.total_stock) || 0,
          lowStockCount: Number(r.low_stock_count) || 0,
          pendingOrders: Number(r.pending_orders) || 0,
          shippedOrders: Number(r.printed_orders) || 0,
          pendingShipments: Number(r.pending_orders) || 0,
          printedShipments: Number(r.printed_orders) || 0,
          unassignedSkuCount: 0,
          todayShipments: Number(r.today_shipments) || 0,
          todayPrinted: Number(r.today_printed) || 0,
          todayPending: Number(r.today_pending) || 0,
          pendingProductImages: Number(r.pending_product_images) || 0,
          stuckOrders: Number(r.stuck_orders) || 0,
        };
      }

      // Fallback legacy (4 queries paralelas)
      const todayStart = startOfDay(new Date()).toISOString();
      const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
      const [products, orders, today, pendingImages] = await Promise.all([
        sq(supabase.from("products").select("id, stock").limit(5000)),
        sq((supabase as any).from("amazon_orderdesk_orders")
          .select("id, our_status, tracking_number, date_added, label_generated_at")
          .gte("date_added", thirtyDaysAgo)
          .limit(5000)),
        sq((supabase as any).from("amazon_orderdesk_orders")
          .select("id, our_status, tracking_number, date_added, label_generated_at")
          .gte("date_added", todayStart)
          .limit(2000)),
        sq(supabase.from("products").select("id").not("asin", "is", null).is("image_url", null).limit(5000)),
      ]);

      const prods = products as any[];
      const ords = orders as any[];
      const todayOrds = today as any[];

      const isPending  = (o: any) => o.our_status === "pending";
      const isPrinted  = (o: any) => o.our_status === "label_generated" || !!o.tracking_number;

      const pendingOrders   = ords.filter(isPending).length;
      const printedOrders   = ords.filter(isPrinted).length;
      const todayPendingNow = todayOrds.filter(isPending).length;
      const todayPrintedNow = todayOrds.filter(isPrinted).length;

      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      const stuckOrders = ords.filter((o) =>
        isPending(o) &&
        !o.tracking_number &&
        o.date_added &&
        new Date(o.date_added).getTime() < cutoff
      ).length;

      return {
        totalProducts: prods.length,
        totalStock: prods.reduce((sum, p) => sum + (p.stock || 0), 0),
        lowStockCount: prods.filter((p) => p.stock > 0 && p.stock <= 5).length,
        pendingOrders,
        shippedOrders: printedOrders,
        pendingShipments: pendingOrders,
        printedShipments: printedOrders,
        unassignedSkuCount: 0,
        todayShipments: todayOrds.length,
        todayPrinted: todayPrintedNow,
        todayPending: todayPendingNow,
        pendingProductImages: (pendingImages as any[]).length,
        stuckOrders,
      };
    },
  });

/**
 * Hook ligero para badges de alerta del sidebar: cuenta crones fallidos en
 * su última ejecución. Refresca cada 5 min. Solo úsalo si el rol es
 * admin/superadmin (es información sensible).
 */
export const useCronAlertCount = (enabled: boolean) =>
  useQuery<number>({
    queryKey: ["cronAlertCount"],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_cron_jobs_with_last_run");
      if (error) return 0;
      const rows = (data || []) as Array<{ last_status: string | null; active: boolean }>;
      return rows.filter((r) => r.active && r.last_status === "failed").length;
    },
  });

export const useOverviewShipmentsByDay = () =>
  useQuery({
    queryKey: ["overviewShipmentsByDay"],
    queryFn: async () => {
      const sevenDaysAgo = subDays(new Date(), 6).toISOString();
      const data = await sq(
        (supabase as any).from("amazon_orderdesk_orders")
          .select("date_added")
          .gte("date_added", sevenDaysAgo)
          .order("date_added")
          .limit(5000),
      );
      // Mantener el nombre del campo que usa Overview.tsx (sendcloud_created_at)
      // para no romper el chart — lo renombro al vuelo.
      return (data as any[]).map((r) => ({ sendcloud_created_at: r.date_added }));
    },
    staleTime: 5 * 60 * 1000,
  });

export const useOverviewCarrierData = () =>
  useQuery({
    queryKey: ["overviewCarrierData"],
    queryFn: async () => {
      const sevenDaysAgo = subDays(new Date(), 7).toISOString();

      // Cuenta envíos por transportista en los últimos 7 días, desde movements
      // de tipo salida (única fuente activa después de la migración a Order Desk).
      const movRes = await sq(
        (supabase as any)
          .from("movements")
          .select("transportista")
          .eq("type", "salida")
          .gte("created_at", sevenDaysAgo)
          .not("transportista", "is", null)
          .limit(10000),
      );

      const carrierMap: Record<string, number> = {};
      ((movRes as any[]) || []).forEach((m) => {
        const raw = (m.transportista || "").toString().trim();
        if (!raw) return;
        carrierMap[raw] = (carrierMap[raw] || 0) + 1;
      });

      return Object.entries(carrierMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
    },
    staleTime: 5 * 60 * 1000,
  });

export const useOverviewLowStock = () =>
  useQuery({
    queryKey: ["overviewLowStock"],
    queryFn: async () => {
      const data = await sq(
        supabase.from("products").select("name, sku, stock")
          .gt("stock", 0).lte("stock", 5)
          .order("stock", { ascending: true }).limit(8)
      );
      return ((data as any[]) || []).map((p) => ({ name: p.name, sku: p.sku || "—", stock: p.stock }));
    },
    staleTime: 5 * 60 * 1000,
  });

export interface PendingInvoiceRow {
  id: string;
  invoice_number: string;
  issue_date: string;
  total_amount: number;
  clients: { name: string } | null;
}

export const useOverviewPendingInvoices = () =>
  useQuery<PendingInvoiceRow[]>({
    queryKey: ["overviewPendingInvoices"],
    queryFn: () =>
      sq(
        supabase
          .from("invoices")
          .select("id, invoice_number, issue_date, total_amount, clients(name)")
          .eq("status", "pending")
          .order("issue_date", { ascending: true })
          .limit(10),
      ) as Promise<PendingInvoiceRow[]>,
    staleTime: 5 * 60 * 1000,
  });

export const useOverviewSystemStatus = () =>
  useQuery({
    queryKey: ["overviewSystemStatus"],
    queryFn: async () => {
      const [cookieRes, errorRes, pendingStorageRes, pendingDriveRes] = await Promise.all([
        supabase.from("app_settings").select("value, updated_at").eq("key", "seller_central_cookie").single(),
        supabase.from("app_settings").select("value, updated_at").eq("key", "seller_central_cookie_error").single(),
        // Pending: no image in Supabase Storage (excluye needs_manual)
        (supabase as any).from("labeling_entries").select("id", { count: "exact", head: true }).is("product_image_url", null).not("order_number", "is", null).eq("needs_manual", false),
        // Pending: not uploaded to Google Drive (excluye needs_manual)
        (supabase as any).from("labeling_entries").select("id", { count: "exact", head: true }).eq("drive_uploaded", false).not("order_number", "is", null).eq("needs_manual", false),
      ]);
      // Show the larger of the two counts (worst case: most incomplete)
      const pendingStorage = pendingStorageRes.count || 0;
      const pendingDrive = pendingDriveRes.count || 0;
      return {
        cookieUpdated: cookieRes.data?.updated_at || null,
        cookieError: errorRes.data?.value || null,
        pendingLabelImages: Math.max(pendingStorage, pendingDrive),
        lastSync: null as string | null,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

// ── Check Invoices (amazon orders grouped by order_number) ──────
export interface OrderItem {
  sku: string;
  productName: string;
  quantity: number;
}
export interface OrderData {
  referencia: string;
  items: OrderItem[];
  totalItems: number;
}

export const useAmazonOrdersMap = () =>
  useQuery({
    queryKey: ["amazonOrdersMap"],
    queryFn: async () => {
      const data = await sq(
        supabase.from("amazon_orders").select("order_number, quantity, product:products(sku, name)")
      );
      const map = new Map<string, OrderData>();
      for (const o of (data as any[]) || []) {
        const referencia = (o.order_number || "").replace(/\//g, "-").trim();
        const sku = o.product?.sku || "";
        const productName = o.product?.name || "";
        const quantity = o.quantity || 1;
        const item: OrderItem = { sku, productName, quantity };

        if (map.has(referencia)) {
          const existing = map.get(referencia)!;
          existing.items.push(item);
          existing.totalItems += quantity;
        } else {
          map.set(referencia, { referencia, items: [item], totalItems: quantity });
        }
      }
      return map;
    },
    staleTime: 10 * 60 * 1000,
  });

// ── Users (via edge function) ────────────────────────────────────
export const useUsers = () =>
  useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-users");
      if (error) throw error;
      return (data?.users || []) as any[];
    },
  });

// ── Labeling entries ─────────────────────────────────────────────
export const useLabelingEntries = () =>
  useQuery({
    queryKey: ["labelingEntries"],
    queryFn: async () => {
      const data = await sq(
        (supabase as any).from("labeling_entries")
          .select("*")
          .not("product_image_url", "is", null)
          .order("created_at", { ascending: false })
          .limit(9)
      );
      return (data as any[]) || [];
    },
  });

export const useProductionQueue = () =>
  useQuery({
    queryKey: ["productionQueue"],
    queryFn: async () => {
      const data = await sq(
        (supabase as any).from("labeling_entries")
          .select("id, label_barcode, sku, order_number, recipient_name, bulto_index, total_bultos, product_image_url, shipment_id, created_at")
          .not("product_image_url", "is", null)
          .order("created_at", { ascending: false })
          .limit(200)
      );
      return (data as any[]) || [];
    },
    staleTime: 60 * 1000,
  });

// ── Pending label images (for Overview dialog) ──────────────────
export interface PendingLabelImage {
  id: string;
  order_number: string;
  sku: string | null;
  recipient_name: string | null;
  bulto_index: number;
  total_bultos: number;
  product_image_url: string | null;
  drive_uploaded: boolean;
  created_at: string;
  needs_manual: boolean;
  needs_manual_reason: string | null;
}

export const usePendingLabelImages = (enabled: boolean) =>
  useQuery<PendingLabelImage[]>({
    queryKey: ["pendingLabelImages"],
    queryFn: async () => {
      const columns =
        "id, order_number, sku, recipient_name, bulto_index, total_bultos, product_image_url, drive_uploaded, created_at, needs_manual, needs_manual_reason";

      // 1) Todas las pendientes (sin imagen en Storage, sin subir a Drive o en revisión).
      //    Sin límite: si hay una vieja que aún no se procesó, tiene que aparecer aquí.
      const pendingData = await sq(
        (supabase as any).from("labeling_entries")
          .select(columns)
          .not("order_number", "is", null)
          .or("product_image_url.is.null,drive_uploaded.eq.false,needs_manual.eq.true")
          .order("created_at", { ascending: false })
      );

      // 2) Últimas 100 entradas completas (ya en Storage y en Drive, sin needs_manual),
      //    para contexto histórico en el diálogo.
      const completeData = await sq(
        (supabase as any).from("labeling_entries")
          .select(columns)
          .not("order_number", "is", null)
          .not("product_image_url", "is", null)
          .eq("drive_uploaded", true)
          .eq("needs_manual", false)
          .order("created_at", { ascending: false })
          .limit(100)
      );

      // Merge único por id, preservando orden: pendientes primero, luego completas.
      const seen = new Set<string>();
      const merged: PendingLabelImage[] = [];
      for (const row of [...((pendingData as any[]) || []), ...((completeData as any[]) || [])]) {
        if (seen.has(row.id)) continue;
        seen.add(row.id);
        merged.push(row as PendingLabelImage);
      }
      return merged;
    },
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });

// ── Helper: invalidate after mutations ────────────────────────────
export const useInvalidate = () => {
  const qc = useQueryClient();
  return (...keys: string[]) => {
    keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
};
